<script setup lang="ts">
import type { StoredAccount } from "@verbleif/shared/src/composables/AccountsStore/useAccountsStore";
import VButton from "@verbleif/lib/src/components/Button/VButton.vue";
import VIconTile from "@verbleif/lib/src/components/IconTile/VIconTile.vue";
import { useDialogLabel } from "@verbleif/lib/src/composables/Dialog/useDialogLabel";
import Avatar from "@verbleif/shared/src/components/Avatar/Avatar.vue";
import { useI18n } from "vue-i18n";

/**
 * Body of the "Je sessie is verlopen" alert: the session of the account this tab works in was rejected. The person
 * continues as another account, signs in again as this one, or signs out. Not dismissible, since the tab has no
 * valid session to fall back to.
 */
const { name, others, busy = false } = defineProps<{
  name: string
  others: StoredAccount[]
  busy?: boolean
}>();

const emit = defineEmits<{
  continueAs: [index: number]
  relogin: []
  logout: []
}>();

const { t } = useI18n();
const TRANSLATION_KEY = "navbar.dropdown";

const titleId = useDialogLabel("title");
const messageId = useDialogLabel("description");
</script>

<template>
  <div class="flex w-full flex-col" data-testid="session-expired">
    <div class="flex flex-col gap-1.5 px-5 pt-5 pb-3">
      <div class="flex items-center gap-3">
        <VIconTile icon="clock" tone="caution" size="md" aria-hidden="true" />
        <h2 :id="titleId" class="m-0 text-base leading-5 font-semibold text-(--text)">
          {{ t(`${TRANSLATION_KEY}.session_expired_title`) }}
        </h2>
      </div>
      <p :id="messageId" class="m-0 text-sm leading-5 text-gray-700 dark:text-gray-300">
        {{ t(`${TRANSLATION_KEY}.session_expired_text`, { name }) }}
      </p>
    </div>

    <ul class="m-0 flex list-none flex-col gap-1.5 px-5 pb-4">
      <li v-for="account in others" :key="account.index">
        <button
          type="button"
          class="flex w-full cursor-pointer items-center gap-3 rounded-xl border border-gray-200 p-2.5 text-left hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-60 dark:border-dark-600 dark:hover:bg-dark-700"
          :disabled="busy"
          data-testid="session-expired-continue"
          @click="emit('continueAs', account.index)"
        >
          <Avatar :name="account.name" :avatar-url="account.avatarUrl" size="sm" />
          <span class="flex min-w-0 flex-col">
            <span class="truncate text-sm leading-5 font-medium text-gray-900 dark:text-gray-100">
              {{ t(`${TRANSLATION_KEY}.continue_as`, { name: account.name }) }}
            </span>
            <span class="truncate text-xs leading-4 text-gray-500 dark:text-gray-400">{{ account.username }}</span>
          </span>
        </button>
      </li>
    </ul>

    <footer class="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/60 px-4 py-3 dark:border-dark-700 dark:bg-dark-800/60">
      <VButton variant="gray" rounded :disabled="busy" data-testid="session-expired-logout" @click="emit('logout')">
        {{ t(`${TRANSLATION_KEY}.logout`) }}
      </VButton>
      <VButton rounded :disabled="busy" data-testid="session-expired-relogin" @click="emit('relogin')">
        {{ t(`${TRANSLATION_KEY}.relogin_as`, { name }) }}
      </VButton>
    </footer>
  </div>
</template>
